import { useState } from "react";
import { Building2, Pencil, Plus, X } from "lucide-react";
import SecondaryButton from "@/shared/components/ui/Button/SecondaryButton";
import { PrimaryButton } from "@/shared/components/ui/Button/PrimaryButton";
import IconButton from "@/shared/components/ui/Button/IconButton";
import TextButton from "@/shared/components/ui/Button/TextButton";
import CommonInput from "@/shared/components/ui/Input/CommonInput";
import { useUpdateWhitespaceVendors } from "@/features/accounts/hooks/useAccounts";

const EMPTY_VENDOR = { name: "", category: "", note: "" };

const CurrentVendors = ({ vendors = [], auditId, disabled = false, userId, companyName }) => {
  // null = view mode; an array = the vendor rows being edited
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState("");
  const saveVendors = useUpdateWhitespaceVendors({ userId, companyName });

  const startEditing = () => {
    setError("");
    setDraft(vendors.length ? vendors.map((vendor) => ({ ...EMPTY_VENDOR, ...vendor })) : [{ ...EMPTY_VENDOR }]);
  };

  const updateRow = (index, field, value) => {
    setError("");
    setDraft((rows) => rows.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const handleSave = () => {
    const rows = draft
      .map((row) => ({ name: row.name.trim(), category: row.category.trim(), note: row.note.trim() }))
      .filter((row) => row.name || row.category || row.note);
    if (rows.some((row) => !row.name)) {
      setError("Every vendor needs a name.");
      return;
    }
    const names = rows.map((row) => row.name.toLowerCase());
    if (new Set(names).size !== names.length) {
      setError("Each vendor can be listed only once.");
      return;
    }
    saveVendors.mutate({ userId, auditId, vendors: rows }, { onSuccess: () => setDraft(null) });
  };

  return (
    <div className="flex flex-col gap-2 py-3 self-stretch">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 font-medium text-[0.793rem] leading-5">
          <Building2 size={16} className="text-gray-800 dark:text-gray-200" />
          Current Vendors
        </div>
        {!draft && (
          <SecondaryButton
            title="Edit"
            icon={<Pencil size={12} />}
            onClick={startEditing}
            disabled={disabled}
            className="px-2.5 py-1 shadow-none"
          />
        )}
      </div>

      {!draft ? (
        vendors.length ? (
          <div className="flex flex-col gap-2">
            {vendors.map((vendor, index) => (
              <div
                key={`${vendor.name}-${index}`}
                className="flex flex-col gap-1 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-3 py-2.5"
              >
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <span className="text-[0.75rem] font-semibold leading-5 text-gray-900 dark:text-gray-100">
                    {vendor.name}
                  </span>
                  {vendor.category && (
                    <span className="text-[0.693rem] leading-[18px] text-gray-500 dark:text-gray-400">
                      {vendor.category}
                    </span>
                  )}
                </div>
                {vendor.note && (
                  <p className="text-[0.693rem] leading-[18px] text-gray-600 dark:text-gray-300 break-words">
                    <span className="font-medium text-gray-800 dark:text-gray-200">Displacement note: </span>
                    {vendor.note}
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[0.693rem] leading-[18px] text-gray-500 dark:text-gray-400">
            No current vendors identified. Use Edit to add the tools this account already uses.
          </p>
        )
      ) : (
        <div className="flex flex-col gap-2">
          {draft.map((row, index) => (
            <div
              key={index}
              className="flex items-start gap-2 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-2.5"
            >
              <div className="grid flex-1 min-w-0 gap-2 sm:grid-cols-2">
                <CommonInput
                  placeholder="Vendor name"
                  aria-label="Vendor name"
                  value={row.name}
                  maxLength={120}
                  onChange={(e) => updateRow(index, "name", e.target.value)}
                  wrapperClassName="min-w-0"
                />
                <CommonInput
                  placeholder="Category, e.g. BI Dashboards"
                  aria-label="Vendor category"
                  value={row.category}
                  maxLength={120}
                  onChange={(e) => updateRow(index, "category", e.target.value)}
                  wrapperClassName="min-w-0"
                />
                <CommonInput
                  placeholder="Displacement note: how to position against this vendor"
                  aria-label="Displacement note"
                  value={row.note}
                  maxLength={600}
                  onChange={(e) => updateRow(index, "note", e.target.value)}
                  wrapperClassName="min-w-0 sm:col-span-2"
                />
              </div>
              <IconButton
                ariaLabel={`Remove ${row.name || "vendor"}`}
                onClick={() => {
                  setError("");
                  setDraft((rows) => rows.filter((_, i) => i !== index));
                }}
                className="hover:text-red-500"
              >
                <X size={14} />
              </IconButton>
            </div>
          ))}

          <TextButton
            onClick={() => setDraft((rows) => [...rows, { ...EMPTY_VENDOR }])}
            disabled={draft.length >= 25}
            className="self-start"
          >
            <span className="inline-flex items-center gap-1">
              <Plus size={12} strokeWidth={2.5} />
              Add vendor
            </span>
          </TextButton>

          {error && <p className="text-[0.693rem] leading-[18px] text-red-500">{error}</p>}

          <div className="flex items-center justify-end gap-2">
            <SecondaryButton
              title="Cancel"
              onClick={() => setDraft(null)}
              disabled={saveVendors.isPending}
              className="shadow-none"
            />
            <PrimaryButton startContent={false} onClick={handleSave} isLoading={saveVendors.isPending}>
              Save
            </PrimaryButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default CurrentVendors;
